/**
 * AI Load Balancer Page
 *
 * Enterprise features:
 * - Real-time provider status with polling
 * - Load balancing strategy configuration
 * - Provider weight adjustment
 * - Circuit breaker status and reset
 * - Failover configuration
 */

import { useState, useMemo, useCallback } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import Modal from '../../components/modals/Modal';
import { l100Api } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useMutation } from '../../hooks/useDataFetch';
import './AILoadBalancer.css';

// Constants
const STATS_REFRESH_INTERVAL = 15000; // 15 seconds - need fast updates for AI status

const AILoadBalancer = () => {
  const { showToast } = useToast();
  const [selectedStrategy, setSelectedStrategy] = useState('weighted');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [providerWeights, setProviderWeights] = useState({});

  // Fetch dashboard stats with polling
  const {
    data: dashboardData,
    isLoading: loadingDashboard,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery(
    () => l100Api.dashboard(),
    {
      key: 'l100:dashboard',
      cacheTTL: 10000,
      refetchInterval: STATS_REFRESH_INTERVAL,
    }
  );

  // Fetch providers
  const {
    data: providersData,
    isLoading: loadingProviders,
    refetch: refetchProviders,
  } = useQuery(
    () => l100Api.providers.list(),
    {
      key: 'l100:providers',
      cacheTTL: 10000,
      refetchInterval: STATS_REFRESH_INTERVAL,
      onSuccess: (data) => {
        // Initialize provider weights
        const weights = {};
        const providers = data?.providers || data?.data || data || [];
        providers.forEach(p => {
          weights[p.id] = p.weight || 0;
        });
        setProviderWeights(weights);
      },
    }
  );

  // Fetch circuit breaker status
  const {
    data: circuitBreakerData,
    isLoading: loadingCircuitBreaker,
    refetch: refetchCircuitBreaker,
  } = useQuery(
    () => l100Api.circuitBreaker.status(),
    {
      key: 'l100:circuitBreaker',
      cacheTTL: 10000,
      refetchInterval: STATS_REFRESH_INTERVAL,
    }
  );

  // Fetch config
  const {
    data: configData,
    isLoading: loadingConfig,
    refetch: refetchConfig,
  } = useQuery(
    () => l100Api.config.get(),
    {
      key: 'l100:config',
      cacheTTL: 30000,
      onSuccess: (data) => {
        if (data?.strategy) {
          setSelectedStrategy(data.strategy);
        }
      },
    }
  );

  // Update provider mutation
  const updateProviderMutation = useMutation(
    ({ id, data }) => l100Api.providers.update(id, data),
    {
      onSuccess: () => {
        refetchProviders();
        setEditingProvider(null);
        showToast('Provider updated', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to update provider', 'error');
      },
    }
  );

  // Disable provider mutation
  const disableProviderMutation = useMutation(
    (id) => l100Api.providers.disable(id),
    {
      onSuccess: () => {
        refetchProviders();
        showToast('Provider disabled', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to disable provider', 'error');
      },
    }
  );

  // Enable provider mutation
  const enableProviderMutation = useMutation(
    (id) => l100Api.providers.enable(id),
    {
      onSuccess: () => {
        refetchProviders();
        showToast('Provider enabled', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to enable provider', 'error');
      },
    }
  );

  // Reset circuit breaker mutation
  const resetCircuitBreakerMutation = useMutation(
    (providerId) => l100Api.circuitBreaker.reset(providerId),
    {
      onSuccess: () => {
        refetchCircuitBreaker();
        refetchProviders();
        showToast('Circuit breaker reset', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to reset circuit breaker', 'error');
      },
    }
  );

  // Update config mutation
  const updateConfigMutation = useMutation(
    (data) => l100Api.config.update(data),
    {
      onSuccess: () => {
        refetchConfig();
        setShowConfigModal(false);
        showToast('Configuration saved', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to save configuration', 'error');
      },
    }
  );

  // Extract data
  const providers = useMemo(() => {
    const data = providersData || {};
    return data.providers || data.data || data.items || [];
  }, [providersData]);

  const circuitBreakers = useMemo(() => {
    const data = circuitBreakerData || {};
    return data.breakers || data.data || [];
  }, [circuitBreakerData]);

  const config = useMemo(() => {
    return configData || {};
  }, [configData]);

  // Compute stats
  const stats = useMemo(() => {
    const data = dashboardData || {};
    return [
      {
        label: 'Total Requests',
        value: formatNumber(data.totalRequests || data.requests24h || 0),
        change: formatPercent(data.requestsGrowth),
        changeType: getChangeType(data.requestsGrowth),
        icon: 'activity',
      },
      {
        label: 'Avg Latency',
        value: `${data.avgLatency || data.latency || 0}ms`,
        change: data.latencyChange ? `${data.latencyChange > 0 ? '+' : ''}${data.latencyChange}%` : undefined,
        changeType: data.latencyChange > 0 ? 'negative' : 'positive',
        icon: 'zap',
      },
      {
        label: 'Success Rate',
        value: `${(data.successRate || data.availability || 99.9).toFixed(1)}%`,
        change: data.successRateChange ? `${data.successRateChange > 0 ? '+' : ''}${data.successRateChange}%` : undefined,
        changeType: data.successRateChange >= 0 ? 'positive' : 'negative',
        icon: 'check-circle',
      },
      {
        label: 'Cost Saved',
        value: formatCurrency(data.costSaved || data.savings || 0),
        change: formatPercent(data.savingsGrowth),
        changeType: getChangeType(data.savingsGrowth),
        icon: 'dollar-sign',
      },
    ];
  }, [dashboardData]);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetchDashboard();
    refetchProviders();
    refetchCircuitBreaker();
    showToast('Data refreshed', 'success');
  }, [refetchDashboard, refetchProviders, refetchCircuitBreaker, showToast]);

  const handleStrategyChange = useCallback((strategy) => {
    setSelectedStrategy(strategy);
    updateConfigMutation.mutate({ strategy });
  }, [updateConfigMutation]);

  const handleWeightChange = useCallback((providerId, weight) => {
    setProviderWeights(prev => ({ ...prev, [providerId]: weight }));
  }, []);

  const handleSaveWeight = useCallback((provider) => {
    const newWeight = providerWeights[provider.id];
    if (newWeight !== provider.weight) {
      updateProviderMutation.mutate({
        id: provider.id,
        data: { weight: newWeight },
      });
    }
  }, [providerWeights, updateProviderMutation]);

  const handleToggleProvider = useCallback((provider) => {
    if (provider.status === 'active') {
      disableProviderMutation.mutate(provider.id);
    } else {
      enableProviderMutation.mutate(provider.id);
    }
  }, [disableProviderMutation, enableProviderMutation]);

  const handleResetCircuitBreaker = useCallback((providerId) => {
    resetCircuitBreakerMutation.mutate(providerId);
  }, [resetCircuitBreakerMutation]);

  // Helpers
  function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K`;
    return num.toLocaleString();
  }

  function formatCurrency(amount) {
    if (!amount) return '$0';
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
    return `$${amount}`;
  }

  function formatPercent(value) {
    if (value === null || value === undefined) return undefined;
    return `${value > 0 ? '+' : ''}${value}%`;
  }

  function getChangeType(value) {
    if (value === null || value === undefined) return 'neutral';
    return value >= 0 ? 'positive' : 'negative';
  }

  function getStatusClass(status) {
    const classes = {
      active: 'status-active',
      degraded: 'status-degraded',
      disabled: 'status-disabled',
      error: 'status-error',
    };
    return classes[status?.toLowerCase()] || 'status-unknown';
  }

  function getCircuitBreakerStatus(providerId) {
    const breaker = circuitBreakers.find(b => b.providerId === providerId);
    return breaker?.state || 'closed';
  }

  const isLoading = loadingDashboard && !dashboardData;

  return (
    <div className="ai-lb-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Load Balancer</h1>
          <p className="page-subtitle">Manage AI provider routing and failover</p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="refresh"
            onClick={handleRefresh}
            disabled={loadingDashboard}
          >
            {loadingDashboard ? 'Syncing...' : 'Refresh'}
          </Button>
          <Button
            variant="secondary"
            icon="settings"
            onClick={() => setShowConfigModal(true)}
          >
            Configure
          </Button>
          <Button variant="primary" icon="plus">
            Add Provider
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {dashboardError && (
        <div className="alert alert-warning">
          <Icon name="alert-circle" size="sm" />
          <span>Failed to load some data. Showing cached information.</span>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>Retry</Button>
        </div>
      )}

      {/* Stats Row */}
      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={isLoading} />
        ))}
      </div>

      {/* Strategy Selection */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Load Balancing Strategy</h3>
        </div>

        <div className="strategy-grid">
          <button
            className={`strategy-card ${selectedStrategy === 'weighted' ? 'active' : ''}`}
            onClick={() => handleStrategyChange('weighted')}
            disabled={updateConfigMutation.isLoading}
          >
            <Icon name="bar-chart" size="lg" />
            <div className="strategy-name">Weighted</div>
            <div className="strategy-desc">Distribute based on configured weights</div>
          </button>

          <button
            className={`strategy-card ${selectedStrategy === 'latency' ? 'active' : ''}`}
            onClick={() => handleStrategyChange('latency')}
            disabled={updateConfigMutation.isLoading}
          >
            <Icon name="zap" size="lg" />
            <div className="strategy-name">Lowest Latency</div>
            <div className="strategy-desc">Route to fastest responding provider</div>
          </button>

          <button
            className={`strategy-card ${selectedStrategy === 'cost' ? 'active' : ''}`}
            onClick={() => handleStrategyChange('cost')}
            disabled={updateConfigMutation.isLoading}
          >
            <Icon name="dollar-sign" size="lg" />
            <div className="strategy-name">Cost Optimized</div>
            <div className="strategy-desc">Minimize cost while meeting SLA</div>
          </button>

          <button
            className={`strategy-card ${selectedStrategy === 'roundrobin' ? 'active' : ''}`}
            onClick={() => handleStrategyChange('roundrobin')}
            disabled={updateConfigMutation.isLoading}
          >
            <Icon name="refresh-cw" size="lg" />
            <div className="strategy-name">Round Robin</div>
            <div className="strategy-desc">Equal distribution across providers</div>
          </button>
        </div>
      </section>

      {/* Providers */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">AI Providers</h3>
          <Button variant="ghost" size="sm" icon="refresh" onClick={refetchProviders}>
            Refresh
          </Button>
        </div>

        <div className="providers-table-container">
          {loadingProviders && providers.length === 0 ? (
            <div className="providers-loading">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="provider-row skeleton">
                  <div className="skeleton-line" style={{ width: '150px' }}></div>
                  <div className="skeleton-badge"></div>
                  <div className="skeleton-line" style={{ width: '80px' }}></div>
                  <div className="skeleton-line" style={{ width: '100px' }}></div>
                </div>
              ))}
            </div>
          ) : providers.length > 0 ? (
            <table className="providers-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Latency</th>
                  <th>Requests (24h)</th>
                  <th>Error Rate</th>
                  <th>Weight</th>
                  <th>Circuit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => {
                  const cbStatus = getCircuitBreakerStatus(provider.id);
                  return (
                    <tr key={provider.id}>
                      <td className="provider-name">
                        <Icon name="cpu" size="sm" />
                        {provider.name}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(provider.status)}`}>
                          {provider.status}
                        </span>
                      </td>
                      <td className="latency-cell">
                        {provider.latency || 0}ms
                        <div className="latency-bar">
                          <div
                            className="latency-fill"
                            style={{ width: `${Math.min((provider.latency || 0) / 6, 100)}%` }}
                          />
                        </div>
                      </td>
                      <td>{formatNumber(provider.requests || provider.requests24h || 0)}</td>
                      <td className={(provider.errorRate || 0) > 0.05 ? 'text-danger' : 'text-success'}>
                        {((provider.errorRate || 0) * 100).toFixed(2)}%
                      </td>
                      <td>
                        <div className="weight-control">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={providerWeights[provider.id] ?? provider.weight ?? 0}
                            className="weight-slider"
                            onChange={(e) => handleWeightChange(provider.id, parseInt(e.target.value))}
                            onMouseUp={() => handleSaveWeight(provider)}
                            onTouchEnd={() => handleSaveWeight(provider)}
                          />
                          <span className="weight-value">
                            {providerWeights[provider.id] ?? provider.weight ?? 0}%
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`circuit-badge circuit-${cbStatus}`}>
                          {cbStatus}
                        </span>
                        {cbStatus === 'open' && (
                          <button
                            className="reset-btn"
                            onClick={() => handleResetCircuitBreaker(provider.id)}
                            disabled={resetCircuitBreakerMutation.isLoading}
                            title="Reset circuit breaker"
                          >
                            <Icon name="refresh-cw" size="xs" />
                          </button>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Button
                            variant="ghost"
                            size="xs"
                            icon={provider.status === 'active' ? 'pause' : 'play'}
                            iconOnly
                            onClick={() => handleToggleProvider(provider)}
                            aria-label={provider.status === 'active' ? 'Disable' : 'Enable'}
                          />
                          <Button
                            variant="ghost"
                            size="xs"
                            icon="edit"
                            iconOnly
                            onClick={() => setEditingProvider(provider)}
                            aria-label="Edit"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="providers-empty">
              <Icon name="cpu" size="xl" />
              <p>No AI providers configured</p>
              <Button variant="primary" icon="plus">
                Add Provider
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Failover Rules */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Failover Configuration</h3>
        </div>

        <div className="failover-settings">
          <div className="failover-item">
            <div className="failover-label">Max Retries</div>
            <input
              type="number"
              className="form-input small"
              value={config.maxRetries || 3}
              readOnly
            />
          </div>
          <div className="failover-item">
            <div className="failover-label">Timeout (ms)</div>
            <input
              type="number"
              className="form-input small"
              value={config.timeout || 5000}
              readOnly
            />
          </div>
          <div className="failover-item">
            <div className="failover-label">Circuit Breaker Threshold</div>
            <input
              type="number"
              className="form-input small"
              value={config.circuitBreakerThreshold || 5}
              readOnly
            />
          </div>
          <div className="failover-item">
            <div className="failover-label">Recovery Time (s)</div>
            <input
              type="number"
              className="form-input small"
              value={config.recoveryTime || 30}
              readOnly
            />
          </div>
        </div>
      </section>

      {/* Config Modal */}
      {showConfigModal && (
        <Modal
          title="Load Balancer Configuration"
          onClose={() => setShowConfigModal(false)}
        >
          <div className="config-form">
            <div className="form-group">
              <label>Max Retries</label>
              <input
                type="number"
                min="0"
                max="10"
                defaultValue={config.maxRetries || 3}
              />
            </div>
            <div className="form-group">
              <label>Request Timeout (ms)</label>
              <input
                type="number"
                min="1000"
                max="30000"
                step="1000"
                defaultValue={config.timeout || 5000}
              />
            </div>
            <div className="form-group">
              <label>Circuit Breaker Threshold</label>
              <input
                type="number"
                min="1"
                max="20"
                defaultValue={config.circuitBreakerThreshold || 5}
              />
            </div>
            <div className="form-group">
              <label>Recovery Time (seconds)</label>
              <input
                type="number"
                min="10"
                max="300"
                defaultValue={config.recoveryTime || 30}
              />
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
                Cancel
              </Button>
              <Button variant="primary">
                Save Configuration
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AILoadBalancer;
